import { useId, useLabels, mergeProps, useSlotId } from "@react-aria/utils";
/**
 * Provides the accessibility implementation for labels and their associated elements.
 * Labels provide context for user inputs.
 * @param props - The props for labels and fields.
 */

export function useLabel(props) {
  var id = props.id,
      label = props.label,
      ariaLabelledby = props['aria-labelledby'],
      ariaLabel = props['aria-label'],
      _props$labelElementTy = props.labelElementType,
      labelElementType = _props$labelElementTy === void 0 ? 'label' : _props$labelElementTy;
  id = useId(id);
  var labelId = useId();
  var labelProps = {};

  if (label) {
    ariaLabelledby = ariaLabelledby ? ariaLabelledby + " " + labelId : labelId;
    labelProps = {
      id: labelId,
      htmlFor: labelElementType === 'label' ? id : undefined
    };
  } else if (!ariaLabelledby && !ariaLabel) {
    console.warn('If you do not provide a visible label, you must specify an aria-label or aria-labelledby attribute for accessibility');
  }

  var fieldProps = useLabels({
    id: id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby
  });
  return {
    labelProps: labelProps,
    fieldProps: fieldProps
  };
}
/**
 * Provides the accessibility implementation for input fields.
 * Fields accept user input, gain context from their label, and may display a description or error message.
 * @param props - Props for the Field.
 */

export function useField(props) {
  var description = props.description,
      errorMessage = props.errorMessage;

  var _useLabel = useLabel(props),
      labelProps = _useLabel.labelProps,
      fieldProps = _useLabel.fieldProps;

  var descriptionId = useSlotId();
  var errorMessageId = useSlotId();
  fieldProps = mergeProps(fieldProps, {
    'aria-describedby': [descriptionId, // Use aria-describedby for error message because aria-errormessage is unsupported using VoiceOver or NVDA. See https://github.com/adobe/react-spectrum/issues/1346#issuecomment-740136268
    errorMessageId, props['aria-describedby']].filter(Boolean).join(' ') || undefined
  });
  var descriptionProps = {};
  var errorMessageProps = {};

  if (description) {
    descriptionProps.id = descriptionId;
  }

  if (errorMessage) {
    errorMessageProps.id = errorMessageId;
  }

  return {
    labelProps: labelProps,
    fieldProps: fieldProps,
    descriptionProps: descriptionProps,
    errorMessageProps: errorMessageProps
  };
}